import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Wrench, ArrowLeft, Mail, Phone, MapPin, Calendar, Archive, ClipboardList, Edit, ArchiveRestore, Trash2 } from 'lucide-react';
import Header from './Header';
import TechnicianForm from './TechnicianForm';
import BrandBadge from './BrandBadge';
import AchievementBadge from './AchievementBadge';
import { useAuth } from '../contexts/AuthContext';
import { 
  formatDate,
  getOrderTypeDisplayText,
  getStatusColor,
  getAllOrders
} from '../lib/orderService';
import { getAllClients } from '../lib/clientService';
import { getAllEquipment } from '../lib/equipmentService';
import { conditionalLog } from '../lib/consoleLogger';

const TechnicianDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userRole } = useAuth();

  // Helper functions to match OrdersList styling
  const getOrderTypeColor = (tipo) => {
    const typeColors = {
      'Reparación': 'bg-blue-50 text-blue-700',
      'Ensamble': 'bg-purple-50 text-purple-700',
      'Garantía': 'bg-orange-50 text-orange-700',
      'Fabricación': 'bg-gray-50 text-gray-700'
    };
    
    return typeColors[tipo] || 'bg-gray-50 text-gray-700';
  };

  const getPaymentStatusColor = (status) => {
    const statusColors = {
      'Sin cargo': 'bg-gray-50 text-gray-700',
      'Esperando pago': 'bg-red-50 text-red-700',
      'Pagado': 'bg-green-50 text-green-700'
    };
    
    return statusColors[status] || 'bg-gray-50 text-gray-700';
  };

  const getRelativeTime = (date) => {
    if (!date) return '';
    
    let orderDate;
    if (date instanceof Date) {
      orderDate = date;
    } else if (date.toDate && typeof date.toDate === 'function') {
      // Firestore Timestamp
      orderDate = date.toDate();
    } else if (typeof date === 'string') {
      // String date from IndexedDB
      orderDate = new Date(date);
    } else if (date && typeof date === 'object' && date.seconds) {
      // Serialized Firestore Timestamp from IndexedDB
      orderDate = new Date(date.seconds * 1000);
    } else {
      // Fallback
      orderDate = new Date(date);
    }
    
    if (isNaN(orderDate.getTime())) {
      return '';
    }
    
    const now = new Date();
    const diffInMs = now - orderDate;
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) {
      return '(hoy)';
    } else if (diffInDays === 1) {
      return '(ayer)';
    } else if (diffInDays < 7) {
      return `(hace ${diffInDays} días)`;
    } else if (diffInDays < 14) {
      return '(hace 1 semana)';
    } else if (diffInDays < 30) {
      const weeks = Math.floor(diffInDays / 7);
      return `(hace ${weeks} semanas)`;
    } else if (diffInDays < 60) {
      return '(hace 1 mes)';
    } else if (diffInDays < 365) {
      const months = Math.floor(diffInDays / 30);
      return `(hace ${months} meses)`;
    } else {
      const years = Math.floor(diffInDays / 365);
      return years === 1 ? '(hace 1 año)' : `(hace ${years} años)`;
    }
  };

  const getClientDisplayName = (order) => {
    if (order.clienteName) {
      return order.clienteName;
    }
    if (order.cliente) {
      return `Cliente: ${order.cliente}`;
    }
    return 'Sin cliente';
  };

  const renderClientInfo = (order) => {
    // For company clients, display company name bold, contact name, and phone on separate lines
    if (order.clienteCompanyName && order.clienteContactName) {
      return (
        <div className="max-w-xs">
          <div className="text-sm font-bold leading-tight truncate" title={order.clienteCompanyName}>
            {order.clienteCompanyName}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400 leading-tight truncate" title={order.clienteContactName}>
            {order.clienteContactName}
          </div>
          {order.clientePhone && (
            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {order.clientePhone}
            </div>
          )}
        </div>
      );
    }
    
    // For individual clients or fallback, use the existing display
    return (
      <div className="max-w-xs">
        <div className="text-sm leading-tight truncate" title={getClientDisplayName(order)}>
          {getClientDisplayName(order)}
        </div>
        {order.clientePhone && (
          <div className="text-xs text-gray-500 truncate">
            {order.clientePhone}
          </div>
        )}
      </div>
    );
  };

  const fetchBrands = async () => {
    try {
      const brandsQuery = query(collection(db, 'marcas'));
      const brandsSnapshot = await getDocs(brandsQuery);
      const brandsList = brandsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setBrands(brandsList);
    } catch (error) {
      conditionalLog('Brands collection may not exist yet, starting with empty list');
      setBrands([]);
    }
  };

  // Helper function to get brand object by ID or name
  const getBrandByNameOrId = (identifier) => {
    if (!identifier || brands.length === 0) {
      return { nombre: identifier || 'Sin marca', color: '#6B7280' };
    }
    
    // Try to find by ID first (new format)
    let brand = brands.find(b => b.id === identifier);
    
    // If not found, try by name (backward compatibility)
    if (!brand) {
      brand = brands.find(b => b.nombre === identifier);
    }
    
    // Return found brand or fallback
    return brand || { nombre: identifier, color: '#6B7280' };
  };

  const getEquipmentSummary = (order) => {
    if (!order.equipos || order.equipos.length === 0) {
      return 'Sin equipos';
    }
    
    return order.equipos.map((equipo, index) => {
      if (equipo.type === 'exempt' || !equipo.catalogData) {
        // For exempt equipment: descripcion, series with S/N label
        return (
          <div key={index} className="text-xs leading-tight break-words">
            <span>{equipo.descripcion}</span>
            {equipo.series && (
              <span> S/N: {equipo.series}</span>
            )}
          </div>
        );
      } else {
        // For catalog equipment: type, brand, model (bold), part number (parenthesis), S/N: serial
        const catalogData = equipo.catalogData;
        return (
          <div key={index} className="text-xs leading-tight break-words">
            {catalogData?.tipo && <span>{catalogData.tipo} </span>}
            {catalogData?.marca && <BrandBadge brand={getBrandByNameOrId(catalogData.marca)} size="xs" className="mr-1" />}
            {catalogData?.modelo && <span className="font-bold">{catalogData.modelo}</span>}
            {catalogData?.numeroParte && <span> ({catalogData.numeroParte})</span>}
            {equipo.series && <span> S/N: {equipo.series}</span>}
          </div>
        );
      }
    });
  };
  const [technician, setTechnician] = useState(null);
  const [orders, setOrders] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [isTechnicianInUse, setIsTechnicianInUse] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    fetchTechnicianData();
    fetchBrands();
  }, [id]);

  // Reset pagination when orders change
  useEffect(() => {
    setCurrentPage(1);
  }, [orders.length]);

  const canEdit = () => {
    return userRole && ['admin', 'shopkeeper'].includes(userRole);
  };

  const canDelete = () => {
    return userRole === 'admin';
  };

  const handleEdit = () => {
    setShowForm(true);
  };

  const handleFormSuccess = () => {
    setSuccess('Técnico actualizado exitosamente');
    setShowForm(false);
    fetchTechnicianData(); // Refresh technician data
    setTimeout(() => setSuccess(''), 5000);
  };

  const handleFormClose = () => {
    setShowForm(false);
  };

  const checkTechnicianInOrders = async (technicianId) => {
    try {
      // Use cached orders data to check technician usage
      const allOrders = await getAllOrders(true); // Include archived orders
      return allOrders.some(order => 
        order.tecnicos && order.tecnicos.includes(technicianId)
      );
    } catch (error) {
      console.error('Error checking technician in orders:', error);
      return true; // Assume technician is in use if there's an error
    }
  };

  const handleArchive = async () => {
    if (!window.confirm(`¿Está seguro de archivar a ${technician.nombre}?`)) return;

    try {
      setActionLoading(true);
      await updateDoc(doc(db, 'technicians', technician.id), {
        isArchived: true,
        updatedAt: new Date()
      });
      setSuccess(`${technician.nombre} ha sido archivado`);
      await fetchTechnicianData();
      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      console.error('Error archiving technician:', error);
      setError('Error al archivar el técnico');
      setTimeout(() => setError(''), 5000);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnarchive = async () => {
    if (!window.confirm(`¿Está seguro de desarchivar a ${technician.nombre}?`)) return;

    try {
      setActionLoading(true);
      await updateDoc(doc(db, 'technicians', technician.id), {
        isArchived: false,
        updatedAt: new Date()
      });
      setSuccess(`${technician.nombre} ha sido desarchivado`);
      await fetchTechnicianData();
      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      console.error('Error unarchiving technician:', error);
      setError('Error al desarchivar el técnico');
      setTimeout(() => setError(''), 5000);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setActionLoading(true);
      // Check if technician is assigned to any orders
      const isInUse = await checkTechnicianInOrders(technician.id);
      
      if (isInUse) {
        setError(`No se puede eliminar a ${technician.nombre} porque tiene órdenes asignadas. Considere archivar el técnico en su lugar.`);
        setTimeout(() => setError(''), 8000);
        return;
      }

      if (!window.confirm(
        `¿Está seguro de eliminar permanentemente a ${technician.nombre}?\n\n` +
        `Esta acción no se puede deshacer. Si solo desea ocultar el técnico, ` +
        `considere archivarlo en su lugar.`
      )) {
        return;
      }

      await deleteDoc(doc(db, 'technicians', technician.id));
      setSuccess(`${technician.nombre} ha sido eliminado permanentemente`);
      
      // Redirect to technicians list after successful deletion
      setTimeout(() => {
        navigate('/technicians');
      }, 2000);
      
    } catch (error) {
      console.error('Error deleting technician:', error);
      setError('Error al eliminar el técnico');
      setTimeout(() => setError(''), 5000);
    } finally {
      setActionLoading(false);
    }
  };

  const fetchTechnicianData = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch technician details directly to ensure we have the latest data
      // (especially for archiving status and recent updates)
      const techDoc = await getDoc(doc(db, 'technicians', id));
      if (!techDoc.exists()) {
        setError('Técnico no encontrado');
        setLoading(false);
        return;
      }

      const techData = { id: techDoc.id, ...techDoc.data() };
      setTechnician(techData);

      // Fetch orders for this technician using local-first cached approach
      try {
        // Get all cached orders (much faster than individual queries)
        const allOrders = await getAllOrders(true); // Include archived orders
        
        // Filter orders for this technician
        const technicianOrders = allOrders.filter(order => 
          order.tecnicos && order.tecnicos.includes(id)
        );
        
        // Get cached client and equipment data
        const [allClients, allEquipment] = await Promise.all([
          getAllClients(true), // Include archived clients
          getAllEquipment(true) // Include archived equipment
        ]);
        
        // Create lookup maps for efficient data population
        const clientsMap = new Map(allClients.map(client => [client.id, client]));
        const equipmentMap = new Map(allEquipment.map(equipment => [equipment.id, equipment]));
        
        // Populate order data with client and equipment information
        const populatedOrders = technicianOrders.map(order => {
          let populatedOrder = { ...order };
          
          // Populate client information from cache
          if (order.cliente && clientsMap.has(order.cliente)) {
            const clientData = clientsMap.get(order.cliente);
            const mainContact = clientData.contactos?.find(c => c.esPrincipal) || clientData.contactos?.[0];
            
            populatedOrder = {
              ...populatedOrder,
              clienteName: mainContact?.nombre || 'Sin nombre',
              clienteCompanyName: clientData.nombreEmpresa || null,
              clienteContactName: mainContact?.nombre || null,
              clientePhone: mainContact?.telefono || null
            };
          }
          
          // Populate equipment catalog data from cache
          if (order.equipos && order.equipos.length > 0) {
            populatedOrder.equipos = order.equipos.map(equipo => {
              if (equipo.catalogId && equipmentMap.has(equipo.catalogId)) {
                return {
                  ...equipo,
                  catalogData: equipmentMap.get(equipo.catalogId),
                  type: 'catalog'
                };
              }
              
              // Return as exempt equipment if no catalog data
              return {
                ...equipo,
                type: 'exempt'
              };
            });
          }
          
          return populatedOrder;
        });
        
        // Sort by folio (descending - highest to lowest)
        const sortedOrders = populatedOrders.sort((a, b) => {
          const folioA = parseInt(a.folio) || 0;
          const folioB = parseInt(b.folio) || 0;
          return folioB - folioA; // Descending order
        });
        
        setOrders(sortedOrders);
      } catch (error) {
        console.error('Error fetching technician orders:', error);
        setOrders([]);
      }
      
      // Check if technician is used in orders
      const inUse = await checkTechnicianInOrders(id);
      setIsTechnicianInUse(inUse);
      
    } catch (error) {
      console.error('Error fetching technician data:', error);
      setError('Error al cargar la información del técnico');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    const months = [
      'ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN',
      'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'
    ];
    
    const day = dateObj.getUTCDate().toString().padStart(2, '0');
    const month = months[dateObj.getUTCMonth()];
    const year = dateObj.getUTCFullYear();
    
    return `${day}/${month}/${year}`;
  };

  const calculateAge = (fechaNacimiento) => {
    if (!fechaNacimiento) return '';
    
    const birthDate = fechaNacimiento.toDate ? fechaNacimiento.toDate() : new Date(fechaNacimiento);
    const today = new Date();
    
    const diffTime = today - birthDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    const years = Math.floor(diffDays / 365);
    const months = Math.floor((diffDays % 365) / 30);
    
    if (years > 0 && months > 0) {
      return `${years} año${years > 1 ? 's' : ''}, ${months} mes${months > 1 ? 'es' : ''}`;
    } else if (years > 0) {
      return `${years} año${years > 1 ? 's' : ''}`;
    } else if (months > 0) {
      return `${months} mes${months > 1 ? 'es' : ''}`;
    } else {
      const days = Math.max(0, diffDays);
      return `${days} día${days !== 1 ? 's' : ''}`;
    }
  };

  const calculateAntiguedad = (fechaIngreso, fechaBaja) => {
    if (!fechaIngreso) return '';
    
    const startDate = fechaIngreso.toDate ? fechaIngreso.toDate() : new Date(fechaIngreso);
    const endDate = fechaBaja ? 
      (fechaBaja.toDate ? fechaBaja.toDate() : new Date(fechaBaja)) : 
      new Date();
    
    const diffTime = endDate - startDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    const years = Math.floor(diffDays / 365);
    const months = Math.floor((diffDays % 365) / 30);
    
    if (years > 0 && months > 0) {
      return `${years} año${years > 1 ? 's' : ''}, ${months} mes${months > 1 ? 'es' : ''}`;
    } else if (years > 0) {
      return `${years} año${years > 1 ? 's' : ''}`;
    } else if (months > 0) {
      return `${months} mes${months > 1 ? 'es' : ''}`;
    } else {
      const days = Math.max(0, diffDays);
      return `${days} día${days !== 1 ? 's' : ''}`;
    }
  };

  // Pagination calculations
  const totalItems = orders.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentOrders = orders.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Header />
        <div className="max-w-6xl mx-auto p-6">
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            Cargando información del técnico...
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Header />
        <div className="max-w-6xl mx-auto p-6">
          <div className="mb-6">
            <Link 
              to="/technicians"
              className="inline-flex items-center space-x-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Volver a Técnicos</span>
            </Link>
          </div>
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-md">
            {error}
          </div>
        </div>
      </div>
    );
  }

  if (!technician) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Header />
      <div className="max-w-6xl mx-auto p-6">
        <div className="mb-6">
          <Link 
            to="/technicians"
            className="inline-flex items-center space-x-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Volver a Técnicos</span>
          </Link>
        </div>

        {/* Success and Error Messages */}
        {success && (
          <div className="mb-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-600 dark:text-green-400 px-4 py-3 rounded-md">
            {success}
          </div>
        )}
        
        {error && (
          <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-md">
            {error}
          </div>
        )}

        {/* Technician Information Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm mb-6">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Wrench className="h-8 w-8 text-orange-600 dark:text-orange-400" />
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{technician.nombre}</h1>
                  <div className="flex items-center flex-wrap gap-2 mt-2">
                    {technician.isArchived && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                        <Archive className="h-3 w-3 mr-1" />
                        Archivado
                      </span>
                    )}
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300">
                      <ClipboardList className="h-4 w-4 mr-1.5" />
                      {orders.length} {orders.length === 1 ? 'orden' : 'órdenes'}
                    </span>
                    <AchievementBadge orderCount={orders.length} />
                  </div>
                </div>
              </div>
              
              {/* Action Buttons */}
              {canEdit() && (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleEdit}
                    disabled={actionLoading}
                    className="p-2 text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded disabled:opacity-50"
                    title="Editar técnico"
                  >
                    <Edit className="h-5 w-5" />
                  </button>
                  
                  {technician.isArchived ? (
                    <button
                      onClick={handleUnarchive}
                      disabled={actionLoading}
                      className="p-2 text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20 rounded disabled:opacity-50"
                      title="Desarchivar técnico"
                    >
                      <ArchiveRestore className="h-5 w-5" />
                    </button>
                  ) : (
                    <button
                      onClick={handleArchive}
                      disabled={actionLoading}
                      className="p-2 text-yellow-600 dark:text-yellow-400 hover:text-yellow-900 dark:hover:text-yellow-300 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 rounded disabled:opacity-50"
                      title="Archivar técnico"
                    >
                      <Archive className="h-5 w-5" />
                    </button>
                  )}
                  
                  {canDelete() && (
                    <button
                      onClick={!isTechnicianInUse ? handleDelete : undefined}
                      disabled={actionLoading || isTechnicianInUse}
                      className={`p-2 rounded disabled:opacity-50 ${
                        isTechnicianInUse
                          ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                          : 'text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20'
                      }`}
                      title={
                        isTechnicianInUse
                          ? 'No se puede eliminar: técnico asignado a órdenes de trabajo'
                          : 'Eliminar técnico'
                      }
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="p-6 bg-white dark:bg-gray-800">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Contact Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Información de Contacto</h3>
                
                <div className="flex items-center space-x-3">
                  <Phone className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Teléfono</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{technician.telefono || 'N/A'}</p>
                  </div>
                </div>

                {technician.email && (
                  <div className="flex items-center space-x-3">
                    <Mail className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Email</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{technician.email}</p>
                    </div>
                  </div>
                )}

                <div className="flex items-start space-x-3">
                  <MapPin className="h-5 w-5 text-gray-400 dark:text-gray-500 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Domicilio</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{technician.domicilio || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Dates Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Fechas Importantes</h3>
                
                <div className="flex items-center space-x-3">
                  <Calendar className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Fecha de Nacimiento</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{formatDate(technician.fechaNacimiento)}</p>
                    {technician.fechaNacimiento && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Edad: {calculateAge(technician.fechaNacimiento)}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Calendar className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Fecha de Ingreso</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{formatDate(technician.fechaIngreso)}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Antigüedad: {calculateAntiguedad(technician.fechaIngreso, technician.fechaBaja)}
                    </p>
                  </div>
                </div>

                {technician.fechaBaja && (
                  <div className="flex items-center space-x-3">
                    <Calendar className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Fecha de Baja</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{formatDate(technician.fechaBaja)}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Status Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Estado</h3>
                
                <div className="space-y-2">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Estado de Archivo</p>
                    <p className={`text-sm ${technician.isArchived ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                      {technician.isArchived ? 'Archivado' : 'Activo'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Order History Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-2">
              <ClipboardList className="h-6 w-6 text-orange-600 dark:text-orange-400" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Historial de Órdenes</h2>
              <span className="bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-xs font-medium px-2.5 py-0.5 rounded-full">
                {orders.length} órdenes
              </span>
            </div>
          </div>

          <div className="p-6 bg-white dark:bg-gray-800">
            {orders.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <ClipboardList className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                <p className="text-lg font-medium text-gray-900 dark:text-white mb-2">Sin órdenes asignadas</p>
                <p>Este técnico no tiene órdenes de trabajo asignadas aún.</p>
              </div>
            ) : (
              <>
                {/* Mobile Card View */}
                <div className="sm:hidden space-y-4">
                  {currentOrders.map((order) => (
                    <div 
                      key={order.id} 
                      className={`border border-gray-200 dark:border-gray-700 rounded-lg p-4 cursor-pointer transition-colors ${
                        order.isArchived 
                          ? 'bg-gray-50 dark:bg-gray-900/50 hover:bg-gray-100 dark:hover:bg-gray-800' 
                          : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                      onClick={() => window.location.href = `/orders/${order.id}`}
                    >
                      {/* Header Row */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          <span className="text-lg font-semibold text-gray-900 dark:text-white">
                            {order.folio}
                          </span>
                          {order.isArchived && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                              Archivada
                            </span>
                          )}
                        </div>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getOrderTypeColor(order.tipo_de_orden)}`}>
                          {getOrderTypeDisplayText(order.tipo_de_orden)}
                        </span>
                      </div>
                      
                      {/* Date */}
                      <div className="mb-3">
                        <div className="text-sm text-gray-900 dark:text-gray-300">
                          {formatDate(order.fecha)}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {getRelativeTime(order.fecha)}
                        </div>
                      </div>
                      
                      {/* Client Info */}
                      <div className="mb-3">
                        <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Cliente:</div>
                        <div className="text-sm text-gray-900 dark:text-gray-300">
                          {renderClientInfo(order)}
                        </div>
                      </div>
                      
                      {/* Equipment Info */}
                      <div className="mb-3">
                        <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Equipos:</div>
                        <div className="text-sm text-gray-900 dark:text-gray-300">
                          <div className="space-y-1">
                            {getEquipmentSummary(order)}
                          </div>
                        </div>
                      </div>
                      
                      {/* Status */}
                      <div className="flex flex-wrap gap-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(order.status_orden)}`}>
                          {order.status_orden || 'Sin estado'}
                        </span>
                        {order.estatus_pago ? (
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPaymentStatusColor(order.estatus_pago)}`}>
                            {order.estatus_pago}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs">Sin estado de pago</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Desktop Table View */}
                <div className="hidden sm:block">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Folio
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Fecha
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Tipo
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden md:table-cell">
                          Cliente
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden lg:table-cell">
                          Equipos
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Estado
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {currentOrders.map((order) => (
                        <tr key={order.id} className={`cursor-pointer ${
                            order.isArchived 
                              ? 'bg-gray-100 dark:bg-gray-900 hover:bg-gray-200 dark:hover:bg-gray-800' 
                              : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                          }`}
                          onClick={() => window.location.href = `/orders/${order.id}`}>
                          <td className="px-3 py-4 text-sm font-medium text-gray-900 dark:text-white">
                            <div className="min-w-0">
                              <div className="font-semibold">
                                {order.folio}
                              </div>
                              {order.isArchived && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 mt-1">
                                  Archivada
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-4 text-sm text-gray-900 dark:text-gray-300">
                            <div>
                              {formatDate(order.fecha)}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {getRelativeTime(order.fecha)}
                            </div>
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getOrderTypeColor(order.tipo_de_orden)}`}>
                              {getOrderTypeDisplayText(order.tipo_de_orden)}
                            </span>
                          </td>
                          <td className="px-3 py-4 text-sm text-gray-900 dark:text-gray-300 hidden md:table-cell">
                            {renderClientInfo(order)}
                          </td>
                          <td className="px-3 py-4 text-sm text-gray-900 dark:text-gray-300 hidden lg:table-cell">
                            <div className="max-w-xs">
                              <div className="space-y-1">
                                {getEquipmentSummary(order)}
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-4 text-sm">
                            <div className="space-y-1">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${getStatusColor(order.status_orden)}`}>
                                {order.status_orden || 'Sin estado'}
                              </span>
                              <div>
                                {order.estatus_pago ? (
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPaymentStatusColor(order.estatus_pago)}`}>
                                    {order.estatus_pago}
                                  </span>
                                ) : (
                                  <span className="text-gray-400 text-xs">—</span>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6">
                  {/* Mobile pagination */}
                  <div className="flex flex-1 justify-between sm:hidden">
                    <button
                      onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Anterior
                    </button>
                    <button
                      onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Siguiente
                    </button>
                  </div>

                  {/* Desktop pagination */}
                  <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        Mostrando <span className="font-medium">{startIndex + 1}</span> a{' '}
                        <span className="font-medium">{Math.min(endIndex, totalItems)}</span> de{' '}
                        <span className="font-medium">{totalItems}</span> órdenes
                      </p>
                    </div>
                    <div>
                      <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                        <button
                          onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                          disabled={currentPage === 1}
                          className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 dark:text-gray-500 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="sr-only">Anterior</span>
                          <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                          </svg>
                        </button>

                        {(() => {
                          const pageNumbers = [];
                          const showEllipsis = totalPages > 7;
                          
                          if (showEllipsis) {
                            if (currentPage <= 3) {
                              for (let i = 1; i <= Math.min(5, totalPages); i++) {
                                pageNumbers.push(i);
                              }
                              if (totalPages > 5) {
                                pageNumbers.push('...');
                                pageNumbers.push(totalPages);
                              }
                            } else if (currentPage >= totalPages - 2) {
                              pageNumbers.push(1);
                              pageNumbers.push('...');
                              for (let i = Math.max(totalPages - 4, 1); i <= totalPages; i++) {
                                if (i > 1) pageNumbers.push(i);
                              }
                            } else {
                              pageNumbers.push(1);
                              pageNumbers.push('...');
                              for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                                pageNumbers.push(i);
                              }
                              pageNumbers.push('...');
                              pageNumbers.push(totalPages);
                            }
                          } else {
                            for (let i = 1; i <= totalPages; i++) {
                              pageNumbers.push(i);
                            }
                          }
                          
                          return pageNumbers.map((number, index) => (
                            number === '...' ? (
                              <span key={`ellipsis-${index}`} className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-300 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:outline-offset-0">
                                ...
                              </span>
                            ) : (
                              <button
                                key={number}
                                onClick={() => handlePageChange(number)}
                                className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 focus:z-20 focus:outline-offset-0 ${
                                  currentPage === number
                                    ? 'z-10 bg-blue-600 dark:bg-blue-700 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 dark:focus-visible:outline-blue-400'
                                    : 'text-gray-900 dark:text-gray-300'
                                }`}
                              >
                                {number}
                              </button>
                            )
                          ));
                        })()}

                        <button
                          onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                          disabled={currentPage === totalPages}
                          className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 dark:text-gray-500 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="sr-only">Siguiente</span>
                          <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* Technician Form Modal */}
      <TechnicianForm
        isOpen={showForm}
        onClose={handleFormClose}
        onSuccess={handleFormSuccess}
        editingTechnician={technician}
      />
    </div>
  );
};

export default TechnicianDetail;